<template>
  <div>
    <fin-data-table :headers="headers"
                    :resolver="instruments"
                    :sort-by="sortByDefault"
                    :sort-desc="sortDescDefault"
                    :sticky-last-col="stickyLastCol"
                    class="my-3"
                    fixed-header>
      <template #header.averagePrice="{ header }">
        <div class="mobile-header">
          <v-icon :size="12" color="#99a0a4">mdi-diameter-variant</v-icon>
          <span class="ml-1 ">{{ header.text }}</span>
        </div>
      </template>
    </fin-data-table>

    <fin-transactions-list-dialog v-if="changeListDialog"
                                  v-model="changeListDialog"
                                  @updatePortfolio="$emit('updatePortfolio')"
                                  :instruments="selectedItem ? [selectedItem.instrument] : undefined"
                                  :portfolio-id="portfolio.id"
                                  :selected-view-id="selectedViewId"
                                  :dialogTitle="transactionDialogTitle"
                                  :full-screen="$vuetify.breakpoint.xs"
                                  :onlyHistory="!editMode"/>


  </div>
</template>

<script>
import {finPortfolioAPI} from '@/js/finAPI'
import FinDataTable from "@/components/common/DataTable/FinDataTable"
import {TablePresets} from "@/components/common/DataTable/DefaultHeaders"
import AmplitudePush from "@/mixins/AmplitudePush"
import FinTransactionsListDialog from "@/components/common/transactions/ListDialog"

export default {
  name: 'FinPortfolioInstruments',
  components: {
    FinTransactionsListDialog,
    FinDataTable,
  },
  props: {
    portfolio: {
      type: Object
    },
    instruments: {
      type: Array
    },
    assetClasses: {
      type: Array
    },
    strategy: {
      type: Object
    },
    /** Title of the transaction dialog */
    transactionDialogTitle: {
      type: String
    },
    editMode: {
      type: Boolean,
      default: true
    },
    stickyLastCol: {
      type: Boolean,
      default: false
    },
    isInactivePortfolio: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      changeListDialog: false,
      selectedItem: undefined,
      sortDescDefault: [true],
      selectedViewId: null
    }
  },
  mixins: [AmplitudePush],
  computed: {
    sortByDefault() {
      return this.assetClass && this.strategy ? ["rank"] : ["col_nameWithIsin"]
    },
    headers() {
      let actions = []

      // don't show any actions for inactive portfolios
      if (this.editMode && !this.isInactivePortfolio) {
        actions.push({
          id: 'BUY',
          name: 'Kaufen',
          disabled: (item) => {
            return (this.portfolio.status && this.portfolio.status.calculationStatus !== 'OK') || (item.instrument && (!item.instrument.active || !item.instrument.covered || item.instrument.delisted))
          },
          click: (item) => {
            this.buy(item)
          },
        })
        actions.push(
          {
            id: 'SELL',
            name: 'Verkaufen',
            disabled: () => {
              return (this.portfolio.status && this.portfolio.status.calculationStatus !== 'OK')
            },
            click: (item) => {
              this.sell(item)
            },
          })
        actions.push(
          {
            id: 'DIVIDENDS',
            name: 'Dividenden',
            click: (item) => {
              this.changeDividend(item)
            }
          })
      }

      // don't show any actions for inactive portfolios
      if (!this.isInactivePortfolio) {
        actions.push({
          id: 'HISTORY',
          name: 'Historie',
          click: (item) => {
            this.change(item)
          }
        })

        if (this.editMode) {
          actions.push({
            id: 'DELETE',
            name: 'Löschen',
            click: (item) => {
              this.confirmRemoveInstrument(item)
            }
          })
        }
      }

      return TablePresets.createPortfolioDataTableHeaders(this.strategy?.id, actions)
    }
  },
  methods: {
    trackToAmplitude(positionOnPage, action) {
      this.pushToAmplitude({
        'event': 'fincraft_portfolio_details_' + positionOnPage + "_" + action,
        'step': action,
        'placement': 'portfolio_details_' + positionOnPage,
        'vue_breakpoint': this.$vuetify.breakpoint.name
      })
    },
    buy(item) {
      this.trackToAmplitude('instruments_table', 'buy')
      this.$router.push({
        name: item.assetClass && item.assetClass.includes('ETF') ?
          'addEtfToPortfolio' : 'addCompanyPageInstrumentToPortfolio',
        params: {
          id: item.instrument.id,
          portfolioId: this.portfolio.id,
          action: 'buy',
          assetClassId: item.assetClass
        }
      })
    },
    sell(item) {
      this.trackToAmplitude('instruments_table', 'sell')
      this.$router.push({
        name: item.assetClass && item.assetClass.includes('ETF') ?
          'addEtfToPortfolio' : 'addCompanyPageInstrumentToPortfolio',
        params: {
          id: item.instrument.id,
          portfolioId: this.portfolio.id,
          action: 'sell',
          assetClassId: item.assetClass
        }
      })
    },
    change(item) {
      this.trackToAmplitude('instruments_table', 'modify_instrument')
      this.selectedItem = item
      this.selectedViewId = 'BUYSELL'
      this.changeListDialog = true
    },
    changeDividend(item) {
      this.trackToAmplitude('instruments_table', 'modify_dividend')
      this.selectedItem = item
      this.selectedViewId = 'DIVIDENDS'
      this.changeListDialog = true
    },
    confirmRemoveInstrument(item) {
      this.trackToAmplitude('instruments_table', 'remove_instrument')
      this.$confirm({
        title: 'Wertpapier löschen?',
        text: `Möchtest du das Wertpapier ${item.instrument.name} wirklich aus deinem Portfolio entfernen?`,
        textWithLexikonLink: false,
        confirmBtn: 'Löschen'
      })
        .then(() => {
          this.deletePortfolioInstrument(item).then(({portfolio, isValid, cause}) => {

            const newInstruments = this.instruments.filter(entry => entry.instrument.id !== item.id)
            if (isValid) {
              this.$emit('update:instruments', newInstruments)
              this.$emit('updatePortfolio', portfolio)
              this.$toasted.global.finToast({
                message: 'Das Wertpapier  %name% wurde erfolgreich aus deinem Portfolio entfernt.',
                placeholders: [{name: 'name', content: item.instrument.name}],
                type: 'success'
              })
            } else {
              this.$toasted.global.finToast({
                message: 'Das Wertpapier %mame% konnte nicht aus deinem Portfolio entfernt werden.' + cause,
                placeholders: [{name: "name", content: item.instrument.name}],
                type: 'error'
              })
            }
          })
            .catch((e) => {
              console.error('deletePortfolioInstrument failed', item, e)
              this.$toasted.global.finToast({
                message: 'Das Wertpapier %mame% konnte nicht aus deinem Portfolio entfernt werden',
                placeholders: [{name: "name", content: item.instrument.name}],
                type: 'error'
              })
            })
        })
    },
    async deletePortfolioInstrument(item) {
      return item.assetClass.includes("ETF") ?
        await finPortfolioAPI.portfolioEtfEntryDelete(this.portfolio.id, item.instrument.id) :
        await finPortfolioAPI.portfolioShareEntryDelete(this.portfolio.id, item.instrument.id)
    }
  },
  watch: {
    changeListDialog() {
      this.selectedItem = this.changeListDialog ? this.selectedItem : null
    }
  },
}
</script>

<style lang="scss" scoped>
::v-deep .action-menu.v-btn--disabled .v-icon {
  color: $fin-color-primary-dark !important;
  opacity: 0.7 !important;
}
</style>
